import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import './LegalPageView.css';

const LegalPageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [legalPage, setLegalPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLegalPage();
  }, [slug]);

  const fetchLegalPage = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/public/legal-pages/${slug}`);
      
      if (response.data.success && response.data.data) {
        const page = response.data.data;
        setLegalPage(page);
        // Update document title and meta tags
        document.title = `${page.title} - Legal Page`;
        
        // Update meta description if available
        if (page.metaDescription) {
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
          }
          metaDesc.setAttribute('content', page.metaDescription);
        }
        
        // Update meta keywords if available
        if (page.metaKeywords) {
          let metaKeywords = document.querySelector('meta[name="keywords"]');
          if (!metaKeywords) {
            metaKeywords = document.createElement('meta');
            metaKeywords.setAttribute('name', 'keywords');
            document.head.appendChild(metaKeywords);
          }
          metaKeywords.setAttribute('content', page.metaKeywords);
        }
      } else {
        setError('Legal page not found');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching legal page:', error);
      if (error.response?.status === 404) {
        setError('Legal page not found');
      } else {
        setError('Failed to load legal page. Please try again later.');
      }
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="legal-page-view-container">
        <div className="legal-page-wrapper">
          <article className="legal-page-content">
            {/* Header Skeleton */}
            <header className="legal-page-header">
              <div className="skeleton skeleton-text" style={{ width: '70%', height: '48px', borderRadius: '8px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '90%', height: '24px', borderRadius: '8px', marginBottom: '8px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '60%', height: '24px', borderRadius: '8px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '16px', borderRadius: '4px' }}></div>
            </header>
            
            {/* Content Skeleton */}
            <div className="legal-page-body">
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '95%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '24px' }}></div>
              
              <div className="skeleton skeleton-text" style={{ width: '60%', height: '32px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '90%', height: '20px', borderRadius: '4px', marginBottom: '24px' }}></div>
              
              <div className="skeleton skeleton-text" style={{ width: '50%', height: '28px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '100%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
              <div className="skeleton skeleton-text" style={{ width: '85%', height: '20px', borderRadius: '4px', marginBottom: '16px' }}></div>
            </div>
          </article>
        </div>
      </div>
    );
  }

  if (error || !legalPage) {
    return (
      <div className="legal-page-view-container">
        <div className="legal-page-error">
          <h1>Page Not Found</h1>
          <p>{error || 'The legal page you are looking for does not exist.'}</p>
          <button onClick={() => navigate('/')} className="btn-back">
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="legal-page-view-container">
      <div className="legal-page-wrapper">
        <article className="legal-page-content">
          <header className="legal-page-header">
            <h1 className="legal-page-title">{legalPage.title}</h1>
            {legalPage.metaDescription && (
              <p className="legal-page-description">{legalPage.metaDescription}</p>
            )}
            <div className="legal-page-meta">
              <span className="last-updated">
                Last updated: {new Date(legalPage.updatedAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
          </header>
          
          <div 
            className="legal-page-body"
            dangerouslySetInnerHTML={{ __html: legalPage.content }}
          />
        </article>
      </div>
    </div>
  );
};

export default LegalPageView;
